import styled from '@emotion/styled';
import { Col, Form, message } from 'antd';
import moment from 'moment-timezone';
import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DEFAULT_TIME } from '../../../../constants/common';
import { PRODUCT_TILE } from '../../../../constants/define/translation';
import {
  ScheduleTilesPayload,
  ScheduleTilesType,
  postScheduleAddUpdateTileWidgetRepository,
} from '../../../../repository/mytm/schedule-tiles/scheduleTilesRepository';
import { appStyles, formScrolling, selectSelector } from '../../../../styles';
import { toDateTime } from '../../../../utils/helper';
import { toDataFormScheduleTiles } from '../../../../utils/transfer/tiles-widget';
import { SpinLoading } from '../../../atoms/loading/SpinLoading';
import { ValidTime } from '../../tour-trash/tab-pane/Details';
import { ScheduleDateTimePickerSection } from './ScheduleDateTimePickerSection';

interface FormScheduleTileProps {
  record?: ScheduleTilesType;
  onClose: () => void;
}

export const FormScheduleTile = (props: FormScheduleTileProps) => {
  const { t } = useTranslation(PRODUCT_TILE);
  const { record, onClose } = props;
  const [startTime, setStartTime] = useState<any>(DEFAULT_TIME);
  const [endTime, setEndTime] = useState<any>(DEFAULT_TIME);
  const [form] = Form.useForm();
  const defaultValue = {
    widgetId: record?.widgetId,
    activeAt: record?.activeAt,
    inActiveAt: record?.inActiveAt,
  };
  const [isCreateLoading, setCreateLoading] = useState(false);
  const [dataForm, setDataForm] = useState<any | undefined>(defaultValue);
  useEffect(() => {
    if (record?.id) {
      const data = toDataFormScheduleTiles(record);
      form.setFieldsValue(data);
      setStartTime(form.getFieldValue('startTime'));
      setEndTime(form.getFieldValue('endTime'));
    }
  }, [record]);
  const onFinish = useCallback(
    async (formValue: any) => {
      const startTimeVal = formValue.startTime;
      const startDateVal = formValue.startDate;
      const endTimeVal = formValue.endTime;
      const endDateVal = formValue.endDate;

      const startDateTime = toDateTime(startDateVal, startTimeVal);
      const endDateTime = toDateTime(endDateVal, endTimeVal);
      const payload: ScheduleTilesPayload = {
        widgetId: record?.widgetId!,
        widgetType: record?.widgetType!,
      };
      if (record?.id) {
        payload.activeAt = startDateVal ? moment(startDateTime).toISOString() : '';
        payload.inActiveAt = endDateVal ? moment(endDateTime).toISOString() : '';
      } else {
        if (startDateVal) {
          payload.activeAt = moment(startDateTime).toISOString();
        }
        if (endDateVal) {
          payload.inActiveAt = moment(endDateTime).toISOString();
        }
      }

      setCreateLoading(true);
      postScheduleAddUpdateTileWidgetRepository(payload)
        .then(result => {
          message.success(t('message.success.scheduleTile'));
          onClose();
          setCreateLoading(false);
        })
        .catch(e => {
          message.error(e.errorMessage);
          setCreateLoading(false);
        });

      // resetFormData();
    },
    [record, dataForm],
  );
  const [validTime, setValidTime] = useState<ValidTime>({});
  const onHandleSetValidTime = useCallback((newData: ValidTime) => {
    setValidTime(oldValue => ({ ...oldValue, ...newData }));
  }, []);

  const onHandleValidateTime = useCallback(
    (start: moment.Moment | null | any, end: moment.Moment | null): void => {
      const startDateVal = form.getFieldValue('startDate');
      const endDateVal = form.getFieldValue('endDate');
      const startDateTime = moment(toDateTime(startDateVal, start));
      const endDateTime = moment(toDateTime(endDateVal, end));

      const now = moment();
      let valid: ValidTime = {
        validateStatus: undefined,
        help: undefined,
        isCheckStartDate: undefined,
      };

      if (startDateVal && endDateVal && start && end) {
        if (startDateTime.isBefore(now)) {
          valid = {
            validateStatus: 'error',
            help: t('tab.detail.form.validate.startBeforeNow'),
            isCheckStartDate: true,
          };
        } else if (endDateTime.isBefore(now)) {
          valid = {
            validateStatus: 'error',
            help: t('tab.detail.form.validate.endBeforeNow'),
            isCheckStartDate: false,
          };
        } else if (startDateTime.isAfter(endDateTime)) {
          valid = {
            validateStatus: 'error',
            help: t('tab.detail.form.validate.startBeforeEnd'),
            isCheckStartDate: true,
          };
        } else {
          valid = {
            validateStatus: 'success',
            help: undefined,
            isCheckStartDate: true,
          };
        }
      }

      onHandleSetValidTime(valid);
    },
    [onHandleSetValidTime],
  );

  const onChangeStartTime = useCallback(
    (value: moment.Moment | null) => {
      setStartTime(value);
      onHandleValidateTime(value, endTime);
    },
    [endTime, onHandleValidateTime],
  );

  const onChangeEndTime = useCallback(
    (value: moment.Moment | null) => {
      setEndTime(value);
      onHandleValidateTime(startTime, value);
    },
    [startTime, onHandleValidateTime],
  );

  const onHandleChangeDate = useCallback(() => {
    onHandleValidateTime(startTime, endTime);
  }, [startTime, endTime, onHandleValidateTime]);

  useEffect(() => {
    if (record) {
      setDataForm({
        ...defaultValue,
      });
    }
  }, [record]);

  return (
    <>
      <SpinLoading isLoading={isCreateLoading} absolute>
        <SwitchContainer>
          <Form
            form={form}
            layout="vertical"
            initialValues={defaultValue}
            onFinish={onFinish}
            name="from-schedule-tile"
          >
            <div className="form-schedule-tiles-container">
              <StyledFormProductTile>
                <div className="form-scroll">
                  <ScheduleDateTimePickerSection
                    form={form}
                    startTime={startTime}
                    endTime={endTime}
                    onChangeStartTime={onChangeStartTime}
                    onChangeEndTime={onChangeEndTime}
                    onHandleChangeDate={onHandleChangeDate}
                    validTime={validTime}
                  />
                </div>
              </StyledFormProductTile>
            </div>
          </Form>
        </SwitchContainer>
      </SpinLoading>
    </>
  );
};

const SwitchContainer = styled.div`
  & .ant-form-item-label {
    font-weight: bold;
    font-size: 15px;
  }

  & .form-ProductTile-container {
    padding: 1.5rem;

    .form-ProductTile__description {
      resize: none;
    }
  }

  & .preview-product-tile {
    border-radius: 18px;
    box-shadow: 0 5px 10px rgba(0, 0, 0, 0.1);
  }
`;

const StyledFormProductTile = styled(Col)`
  ${formScrolling}
  ${selectSelector}

  & input[type='text'],
  & input[type='number'] {
    ${appStyles.inputStyle}
  }

  & .add-partner {
    float: right;
    color: #3490ff;
    font-weight: bold;
    font-size: 14px;
    position: absolute;
    right: 0;
    z-index: 9999;
    background-color: transparent;
    border: none;
    cursor: pointer;
    text-decoration: underline;
    display: inline;
  }

  & .ant-form-item-label {
    font-weight: bold;
    font-size: 16px;
  }

  & button.btn-submit-form-home {
    ${appStyles.commonButtonSubmitted}
  }

  & .ant-form-item {
    margin-bottom: 0.5rem;
  }

  & .ant-picker {
    width: 100%;
    padding: 0;
    ${appStyles.commonBorderRadius}
    & input {
      padding: 4px 0 4px 12px;
    }
    & .ant-picker-suffix {
      font-size: 20px;
      ${appStyles.background.black}
      ${appStyles.color.white}
      padding: 8px;
    }
  }

  & textarea {
    ${appStyles.commonBorderRadius}
  }

  & .ant-input-affix-wrapper {
    padding: 0 11px;
    ${appStyles.commonBorderRadius}
  }

  & .ant-form-item {
    ${appStyles.commonItemMarginBottom}
  }

  & .ant-form-item-explain {
    margin: 0;
  }

  & .ant-picker-clear {
    right: 40px;
  }

  & .ant-descriptions {
    cursor: default;
    & .ant-descriptions-header {
      margin-bottom: 5px;
    }
  }

  & .popover {
    position: absolute;
    z-index: 2;
    bottom: 0;
    left: 10%;
  }

  & .popoverSub {
    position: absolute;
    z-index: 2;
    bottom: 0;
    right: 0;
  }

  & .cover {
    position: fixed;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
  }

  & .sketch-picker {
    cursor: pointer;
  }
`;
